import React, { Suspense, useContext, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import { LoadingContainer } from "go-core/components/Loading";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import ReportData from "go-report/reports";
import {
	getChartReport,
	getLastMonthFilterBetween,
	getOrganizationIds,
	getOrganizationIdsFromReportFetchRequest,
	getReport,
	getReportFilter,
	parseOrganizationIds,
} from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectUser } from "go-security/services/users/selectors";
import { UserState } from "go-security/services/users/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiReport } from "../../../../../../../../services/Api/Report/apiReport";
import { ReportEmployeesUtils } from "../../../../../../../../utils/reports/ReportEmployeesUtils";
import ReportShareUtils from "../../../../../../../../utils/reports/ReportShareUtils";

interface Props {
	resource?: Record<string, any>;
	setOrganizationsIds: (organizationIds: number[]) => void;
	user: UserState;
}

const listName = "EFFICIENCY_REPORT_GROUP";
const resourceType = "EFFICIENCY_REPORT_GROUP";

const Report = ({ resource, setOrganizationsIds, user }: Props) => {
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);
	const filtersParamsRef = useRef<string | undefined>(undefined);

	if (!resource) return null;
	const data = resource.read();

	const type = "work_time";

	let config = {
		reportConfigId: "employees",
		aggregatePrefix: "sales",
		type: "order",
		defaultGroup: "NONE",
		chart: ReportEmployeesUtils.getChartConfig(t),
		groups: [...ReportShareUtils.getGroupReportCommonGroups(t), ...ReportEmployeesUtils.getGroups(t)],
		selectedColumns: ReportEmployeesUtils.getSelectedColumns(),
		selectedGroups: ["DAY_OF_WEEK"],
		groupsInfo: ReportEmployeesUtils.getGroupsInfo(t),
		showPercent: true,
		columns: ReportEmployeesUtils.getColumns(t),
		externalSelectedFilters: [
			{
				id: "date_range",
				filterType: "date",
				value: getLastMonthFilterBetween().split("=")[1],
				condition: getLastMonthFilterBetween().split("=")[0],
				visible: true,
			},
			{
				id: "organization_ids",
				filterType: "list",
				condition: "e",
				value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
				visible: true,
			},
		],
		filters: [
			...ReportEmployeesUtils.getFilters(t),
			{
				id: "date_range",
				name: t("common.word.date", { ns: "lib" }),
				type: "date",
				options: ["bt"],
				isRemovable: false,
			},
			{
				id: "organization_ids",
				name: t("common.word.venue", { ns: "lib" }),
				type: "list",
				options: ReportShareUtils.getOrganizations(user.organizations),
				sortAlphabetically: true,
				hasDefaultOptions: false,
			},
			{
				id: "employee",
				name: t("common.word.employee"),
				type: "label_search_select",
				source: {
					request: (queryString?: string, params?: Record<string, any>) =>
						getReportFilter(
							apiReport.getReportFilter,
							"EMPLOYEE",
							queryString,
							filtersParamsRef.current,
							"work_time",
							undefined,
							params
						),
				},
			},
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				columns: ReportEmployeesUtils.getSelectedColumns(),
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween().split("=")[1],
						condition: getLastMonthFilterBetween().split("=")[0],
					},
					{
						filterId: "organization_ids",
						filterType: "list",
						condition: "e",
						value: getOrganizationIds(user.organizations?.map((x) => x.id).join(FILTER_VALUE_SEPARATOR)),
					},
				],
			},
		],
		fetch: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			filtersParamsRef.current = params.f;
			setOrganizationsIds(getOrganizationIdsFromReportFetchRequest(params.f));
			return Promise.all([
				getReport(
					apiReport.getReportsCustom,
					params,
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					type,
					sourceToken,
					params.groups === "NONE"
				),
				getChartReport(
					apiReport.getReportsCustom,
					params,
					"NONE",
					getOrganizationIdsFromReportFetchRequest(params.f),
					config.filters,
					type,
					sourceToken
				),
			]);
		},
		fetchChartData: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return getChartReport(
				apiReport.getReportsCustom,
				params,
				"NONE",
				getOrganizationIdsFromReportFetchRequest(params.f),
				config.filters,
				type,
				sourceToken
			);
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		exportConfig: {
			title: t("modules.efficiency_report.field.efficiency_export.title"),
			filename: t("modules.efficiency_report.field.efficiency_export_filename.title"),
			pdfOrientation: "ASK",
		},
		withCompareDateRange: true,
	} as ReportConfig;

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

export const UserReportsEmployeeEfficiencyPage = (): JSX.Element => {
	const user = useSelector(selectUser);
	const [organizationsIds, setOrganizationsIds] = useState<number[]>(() =>
		parseOrganizationIds(user.organizations ?? [])
	);
	const organizationIdsAsString = organizationsIds.join();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);

	useEffect(() => {
		const params: Record<string, any> = {};
		if (organizationsIds?.length > 0) {
			params.organization_id = organizationIdsAsString;
		}

		setResource(wrapPromise(segmentContext.get(listName, resourceType, undefined, params)));
	}, [organizationIdsAsString]);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Report resource={resource} setOrganizationsIds={setOrganizationsIds} user={user} />
		</Suspense>
	);
};
